import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useHandleGroupSearch from "../../../pages/deviceManagement/useHandleGroupSearch";
import { getAllNodeValues } from "../../treeView/util";
import {
  IAlertTypes,
  ICountyDetails,
  IGroupAlerts,
  ISelectGroupState,
} from "../../../common/types/alertsTypes";
import { ITreeNode } from "../../../common/types/commonTypes";
import {
  selector as listAlertTypesApi,
} from "../../../pages/integrations/das/createDas/listAlertTypesSlice";
import { editSubscription,selector as editSubscriptionAPI } from "./editSubscriptionSlice";
import { IDasSubscription } from "../../../common/types/dasTypes";
import {
  IEditSubscriptionPayload,
  IAlertType,
  ICounty,
} from "../../../common/types/dasTypes";
import { fetchDasSubscriptionsData } from "../../../pages/integrations/das/listDas/listDasSlice";
import useNotification from "../../notification/useNotification";
import { isSuccessResponse } from "../../../common/utils";
import { useSpinner } from "../../../customHooks";

export const useEditSubscription = (item: IDasSubscription) => {
  const dispatch: any = useDispatch();
  const alertsOptions: IAlertType[] =
    useSelector(listAlertTypesApi).alertTypes;
  const countyOptions: ICountyDetails[] =
    useSelector(listAlertTypesApi).countyDetails;
  const editSubscriptionResponse = useSelector(editSubscriptionAPI);
  useSpinner(editSubscriptionResponse.status);

  const { handleGroupInputChange, groupSearchQuery, nodesToDisplay } =
    useHandleGroupSearch({ isEditable: false });

  const initialCreateSelectGroupState: ISelectGroupState = {
    groups: item.groups,
    checked: item.groups.map((group) => group.id),
    selectAllChecked: false,
  };

  const initialAlertPlaceholder: string = `(${item.alertType.filter((item) => item.name !== "Select All").length
    }) Alert Types Selected`;

  const initialCountyOption: ICounty[] = [
    { name: item.county, value: item.fips, state: item.state },
  ];

  const initialGroupPlaceholder: string =
    item.groups.length > 0
      ? `(${item.groups.length}) Groups are Seleted`
      : "Select Value";

  const [selectedAlertsOptions, setSelectedAlertsOptions] = useState<
    IAlertType[]
  >(item.alertType);
  const [alertsPlaceholder, setAlertsPlaceholder] = useState<string>(
    initialAlertPlaceholder
  );

  const [selectedCountyOptions, setSelectedCountyOptions] =
    useState<ICounty[]>(initialCountyOption);
  const [countyPlaceholder, setCountyPlaceholder] = useState<string>(
    `${item.county} ( ${item.state} )`
  );

  const [selectGroupState, setSelectGroupState] = useState<ISelectGroupState>(
    initialCreateSelectGroupState
  );
  const [groupsPlaceholder, setGroupsPlaceholder] = useState<string>(
    initialGroupPlaceholder
  );

  const [sendAutomatically, setSendAutomatically] = useState<boolean>(
    !item.needsApproval
  );

  const [selectedTemplate, setSelectedTemplate] = useState<number>(
    Number(item.templateId)
  );

  const [selectAllChecked, setSelectAllChecked] = useState<boolean>();

  const [selectedGroups, setSelectedGroups] = useState<IGroupAlerts[]>();

  const configureInitialState = () => {
    setSelectedAlertsOptions(item.alertType);
    setAlertsPlaceholder(initialAlertPlaceholder);
    setSelectedCountyOptions(initialCountyOption);
    setCountyPlaceholder(`${item.county} ( ${item.state} )`);
    setSelectGroupState(initialCreateSelectGroupState);
    setGroupsPlaceholder(initialGroupPlaceholder);
    setSendAutomatically(!item.needsApproval);
    setSelectedTemplate(Number(item.templateId));
    setSelectAllChecked(selectGroupState.selectAllChecked);
    setSelectedGroups(selectGroupState?.groups);
  };
 
  useEffect(() => {
    configureInitialState();
  }, [item]);

  const handleRadioChange = (value: boolean) => {
    setSendAutomatically(value);
  };

  const isTemplateDivSelected = (templateId: number) =>
    selectedTemplate === templateId;

  const handleTemplateClick = (templateId: number) => {
    const newSelectedTemplate =
      selectedTemplate === templateId ? null : templateId;
    setSelectedTemplate(newSelectedTemplate);
  };

  const handleDataFromTreeView = (selectedGroup) => {
    const groups: IGroupAlerts[] = getAllValuesWithLabels(selectedGroup);
    setSelectedGroups(groups);
    const allNodeValues: string[] = getAllNodeValues(
      selectedGroup,
      selectGroupState.selectAllChecked
    );
    setSelectGroupState({
      ...selectGroupState,
      groups: groups,
      checked: allNodeValues,
    });
    setGroupsPlaceholder(
      groups.length > 0
        ? `(${groups.length}) Groups are Seleted`
        : "Select Value"
    );
  };

  const getAllValuesWithLabels = (
    nodes: ITreeNode[] | IGroupAlerts[]
  ): IGroupAlerts[] => {
    let result: IGroupAlerts[] = [];
    function traverse(node) {
      result.push({
        id: node.value || node.id,
        displayName: node.label || node.displayName,
      });
      if (selectGroupState?.selectAllChecked) {
        if (node.children && node.children.length > 0) {
          for (const child of node.children) {
            traverse(child);
          }
        }
      }
    }
    for (const node of nodes) {
      traverse(node);
    }
    return result;
  };

  const handleSelectAllChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const isChecked: boolean = event.target.checked;
    if (isChecked) {
      setSelectAllChecked(true);
      setSelectGroupState({
        ...selectGroupState,
        selectAllChecked: true,
      });
    } else {
      setSelectAllChecked(false);
      setSelectGroupState({
        ...selectGroupState,
        selectAllChecked: false,
      });
    }
  };

  const handleAlertsSelect = (selectedList: any, selectedItem: any) => {
    const placeHolderValue =
      selectedList.length > 0
        ? selectedList[0].name === "Select All"
          ? `(${alertsOptions.slice(0).length - 1}) Alert Types Selected`
          : `(${selectedList.filter((item) => item.name !== "Select All").length
          }) Alert Types Selected`
        : "Select Status";

    if (selectedItem.name === "Select All") {
      setAlertsPlaceholder(
        `(${alertsOptions.filter((item) => item.name !== "Select All").length
        }) Alert Types Selected`
      );
      if (selectedList.length === alertsOptions.length) {
        setSelectedAlertsOptions(alertsOptions.slice(0));
      } else {
        setSelectedAlertsOptions(alertsOptions.slice(0));
      }
    } else {
      setAlertsPlaceholder(placeHolderValue);
      setSelectedAlertsOptions(
        selectedList.filter((item) => item.name !== "Select All")
      );
    }
  };

  const handleAlertsRemove = (selectedList: any, selectedItem: any) => {
    if (selectedItem.name === "Select All") {
      setAlertsPlaceholder("Select Status");
      setSelectedAlertsOptions([]);
    } else {
      const placeholder: string =
        selectedList.length > 0
          ? `(${selectedList.length}) Alert Types Selected`
          : "Select Status";
      setSelectedAlertsOptions(selectedList);
      setAlertsPlaceholder(placeholder);
    }
    const check =
      selectedList.length > 0
        ? selectedList[0].name === "Select All"
          ? true
          : false
        : false;
    if (check) {
      setSelectedAlertsOptions(
        selectedList.filter((item) => item.name !== "Select All")
      );
      setAlertsPlaceholder(
        `(${selectedList.filter((item) => item.name !== "Select All").length
        }) Alert Types Selected`
      );
    }
  };

  const handleCountySelect = (
    selectedItem: ICountyDetails,
    isChecked: boolean
  ) => {
    if (isChecked) {
      setSelectedCountyOptions([selectedItem]);
      setCountyPlaceholder(`${selectedItem.name} ( ${selectedItem.state} )`);
    } else {
      setSelectedCountyOptions([]);
      setCountyPlaceholder("Select Value");
    }
  };

  const { showSuccessNotification, showWarningNotification } =
    useNotification();

  const handleSubmit = () => {
    const editSubscriptionPayload: IEditSubscriptionPayload = {
      subscriptionId: item._id,
      body: {
        alertType: selectedAlertsOptions,
        groups: selectedGroups,
        templateId: selectedTemplate,
        needsApproval: !sendAutomatically,
        county: selectedCountyOptions[0]?.name,
        state: selectedCountyOptions[0]?.state,
      },
    };
    dispatch(editSubscription(editSubscriptionPayload)).then((response) => {
      if (isSuccessResponse(response.payload)) {
        dispatch(fetchDasSubscriptionsData());
        showSuccessNotification(response.payload.message);
      } else {
        showWarningNotification(response.payload.response.data.message);
      }
    });
  };

  const isSaveClickable = useMemo((): boolean => {
    return (
      selectedAlertsOptions?.length > 0 &&
      selectedCountyOptions?.length > 0 &&
      selectedGroups?.length > 0 &&
      selectedTemplate > 0
    );
  }, [
    selectedAlertsOptions,
    selectedCountyOptions,
    selectedGroups,
    selectedTemplate,
  ]);

  return {
    handleRadioChange,
    sendAutomatically,
    isTemplateDivSelected,
    handleTemplateClick,
    handleSubmit,
    handleAlertsSelect,
    handleAlertsRemove,
    alertsOptions,
    selectedAlertsOptions,
    handleGroupInputChange,
    groupSearchQuery,
    nodesToDisplay,
    handleDataFromTreeView,
    selectAllChecked,
    handleSelectAllChange,
    selectGroupState,
    setSelectGroupState,
    alertsPlaceholder,
    countyOptions,
    handleCountySelect,
    countyPlaceholder,
    selectedCountyOptions,
    selectedGroups,
    selectedTemplate,
    groupsPlaceholder,
    isSaveClickable,
  };
};
